import { GTMSignupTriggers } from '@localstack/constants';
import {
  OrganizationService,
  OrganizationsService,
  pushGTMEvent,
  SubscriptionService,
  useApiGetter,
  useApiEffect,
  UserService,
  MembershipService,
} from '@localstack/services';
import { ContentModal } from '@localstack/ui';
import { Box, CircularProgress, Typography } from '@mui/material';
import { ReactElement, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { FinishSignupRequest, PlannedUsageType } from '@localstack/types';

import { BasicPageLayout } from '~/layouts';
import { useAuthProvider } from '~/hooks/useAuthProvider';

import { AuthQueryParamKeys, PlanParamValues, usePostAuthRedirect } from '../common';

import { PersonalDetailsStep, SignUpStudentVerificationFailedErrorBox } from './components';
import { FinishAccountCreationFormData, PlanChoice } from './types';
import {
  getEmulatorTypeFromQueryParams,
  getPlannedUsageTypeFromQueryParams,
  getPromoCodeFromQueryParam,
  resolvePlanChoice,
} from './utils';

export const Onboarding = (): ReactElement => {
  const { userInfo, reloadUserInfo } = useAuthProvider();
  const { redirect } = usePostAuthRedirect();
  const { search } = useLocation();

  const queryParams = useMemo(() => new URLSearchParams(search), [search]);
  const validatedPromoCode = getPromoCodeFromQueryParam(queryParams);
  const planQueryParam = queryParams.get(AuthQueryParamKeys.PLAN);
  const validatedEmulatorType = getEmulatorTypeFromQueryParams(queryParams);

  const isFreemiumPlanSignup = planQueryParam === PlanParamValues.FREE;
  const isStudentPlanSignup = planQueryParam === PlanParamValues.STUDENT;
  const isAWSMarketplaceSignup = planQueryParam === PlanParamValues.MARKETPLACE;

  const defaultValues = {
    newsletter: false,
    emulator: validatedEmulatorType,
  };
  const partialUser: Partial<FinishAccountCreationFormData> = useMemo(
    () => ({
      firstname: userInfo?.user.firstname,
      lastname: userInfo?.user.lastname,
      email: userInfo?.user.email,
      ...defaultValues,
    }),
    [userInfo],
  );

  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting: isSubmitting },
  } = useForm<FinishAccountCreationFormData>({
    mode: 'all',
    defaultValues,
  });
  const email = watch('email');

  const { data: primaryMembership, isLoading: isMembershipLoading } = useApiGetter(
    MembershipService,
    'getPrimaryMembership',
    [],
  );
  const { data: organization, isLoading: isOrganizationLoading } = useApiGetter(
    OrganizationService,
    'getOrganization',
    [],
  );

  const isNotOrgOwner = !primaryMembership?.is_creator;
  // will be true for users joining existing orgs with a non-onboarded account

  const isEnterpriseSSOSignup = !!Object.keys(organization?.sso_settings || {}).length;
  const passedStudentCheck = !!userInfo?.user.signup_survey?.is_student;
  const isStudentWithVerificationFailed = isStudentPlanSignup && !passedStudentCheck;
  const showFreemiumFlow = isFreemiumPlanSignup || isStudentWithVerificationFailed;
  const showStudentPlanFlow = isStudentPlanSignup && passedStudentCheck;
  const showPlanSelection = !isNotOrgOwner && !isEnterpriseSSOSignup && !showStudentPlanFlow && !isAWSMarketplaceSignup;

  const defaultPlanChoice = showFreemiumFlow ? PlanChoice.FREE : PlanChoice.TRIAL;
  const [pickedPlanChoice, setPickedPlanChoice] = useState<PlanChoice>(defaultPlanChoice);
  const planChoice = resolvePlanChoice(watch('emulator'), pickedPlanChoice, defaultPlanChoice);

  const { finishSignup, isLoading: isFinishingSignup } = useApiEffect(UserService, ['finishSignup'], {
    revalidate: ['getUser'],
    revalidateOtherClient: {
      client: [OrganizationsService, MembershipService, SubscriptionService],
      methods: [
        /* User primary membership containing org details */
        'getPrimaryMembership',

        /* OrganizationsService methods */
        'listLicenseAssignments',
        'listLicenseAssignmentsForUser',

        /* SubscriptionService methods */
        'listSubscriptions',
        'getSubscription',
        'listInvoices',
        'listPlans',
      ],
    },
  });

  const isDataLoading = isMembershipLoading || isOrganizationLoading || isSubmitting || isFinishingSignup;

  const onSubmit = async (data: FinishAccountCreationFormData) => {
    const { emulator, ...rest } = data;
    const getPlannedUsageType = () => {
      if (!showPlanSelection) return getPlannedUsageTypeFromQueryParams(queryParams, userInfo);
      return planChoice === PlanChoice.FREE ? PlannedUsageType.FREEMIUM : undefined; // undefined defaults to trial on the backend
    };
    const getLegalAcceptanceContext = (): string => {
      // Branch order matters: the !showPlanSelection paths (student / marketplace / enterprise SSO / invited member)
      // must take precedence over the planChoice split, so that a stray ?plan=free in those flows cannot
      // mislabel the acceptance — see legal-framework docs.
      if (showStudentPlanFlow) return 'accepted terms during Student plan onboarding';
      if (isAWSMarketplaceSignup) return 'accepted terms during AWS Marketplace onboarding';
      if (isEnterpriseSSOSignup) return 'accepted terms during enterprise SSO onboarding';
      if (isNotOrgOwner) return 'accepted terms during invited-member onboarding';
      if (planChoice === PlanChoice.FREE) return 'accepted terms by choosing the Hobby plan during onboarding';
      if (planChoice === PlanChoice.PREVIEW) return 'accepted terms by joining the public preview during onboarding';
      return 'accepted terms by choosing the Trial plan during onboarding';
    };
    const signupSurveyData = {
      emulator,
      is_student: showStudentPlanFlow,
      promo_code: validatedPromoCode,
      planned_usage_type: getPlannedUsageType(),
    };
    const finishSignupRequestData: FinishSignupRequest = {
      ...rest,
      signup_survey: signupSurveyData,
      legal_acceptance_context: getLegalAcceptanceContext(),
    };

    const finishedSignup = await finishSignup(finishSignupRequestData);
    if (!finishedSignup) return;

    pushGTMEvent(GTMSignupTriggers.ONBOARDED, posthog);
    const freshUserInfo = await reloadUserInfo();
    if (freshUserInfo) redirect(freshUserInfo);
  };

  useEffect(() => {
    const defaultValuesLoaded = !!email;
    if (defaultValuesLoaded) return;

    const isUserLoaded = !!partialUser?.email;
    if (isUserLoaded) {
      reset(partialUser);
    }
  }, [partialUser]);

  return (
    <BasicPageLayout title="Welcome to LocalStack" variant="compact">
      <ContentModal
        modalMaxWidth="xs"
        open={true}
        fullWidth
        backdropBlurIntensity={3}
        dialogContent={
          <Box textAlign="center">
            {isDataLoading ? (
              <CircularProgress color="primary" />
            ) : (
              <>
                <Typography variant="h3" component="h2" fontWeight={600}>
                  Set up your account
                </Typography>
                <form onSubmit={handleSubmit(onSubmit)}>
                  {isStudentWithVerificationFailed && <SignUpStudentVerificationFailedErrorBox />}
                  <PersonalDetailsStep
                    control={control}
                    planChoice={planChoice}
                    onPlanChoiceChange={setPickedPlanChoice}
                    isSigningUp={isFinishingSignup}
                    showPlanSelection={showPlanSelection}
                    showStudentPlanFlow={showStudentPlanFlow}
                  />
                </form>
              </>
            )}
          </Box>
        }
      />
    </BasicPageLayout>
  );
};
